import { useState } from 'react';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PageHeader, DataTable, StatusChip, EmptyState, Column } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import SectionCard from '@/trio-ui/SectionCard';
import { FxBankConnection, FxBankAccount } from '@/types/api/finance';

type Props = {
  connections: FxBankConnection[];
  isLoading: boolean;
  syncingId: string | null;
  syncResult: {
    created: number;
    updated: number;
    categorized?: { byRules: number; byAi: number };
  } | null;
  onSync: (connectionId: string, startDate?: string, endDate?: string) => void;
  onManageIntegrations: () => void;
};

const accountColumns: Column<FxBankAccount>[] = [
  { id: 'name', label: 'Account Name', render: (row) => row.name },
  {
    id: 'accountType',
    label: 'Type',
    render: (row) => (
      <StatusChip
        status={row.accountType}
        statusMap={{
          checking: { label: 'Checking', color: '#054AD9' },
          savings: { label: 'Savings', color: '#10B981' },
          credit_card: { label: 'Credit Card', color: '#F59E0B' },
          line_of_credit: { label: 'Line of Credit', color: '#8B5CF6' },
        }}
      />
    ),
  },
  {
    id: 'accountNumberLast4',
    label: 'Last 4',
    render: (row) => (row.accountNumberLast4 ? `••••${row.accountNumberLast4}` : '—'),
  },
  {
    id: 'currentBalance',
    label: 'Balance',
    align: 'right' as const,
    render: (row) =>
      row.currentBalance != null
        ? `${row.currentBalance < 0 ? '-' : ''}$${Math.abs(row.currentBalance).toLocaleString(
            'en-US',
            { minimumFractionDigits: 2 }
          )}`
        : '—',
  },
];

function formatLastSynced(dateStr: string | null): string {
  if (!dateStr) return 'Never synced';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function defaultStartDate(): string {
  return new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
}

function defaultEndDate(): string {
  return new Date().toISOString().split('T')[0];
}

const FinanceXBankAccountsComponent = function ({
  connections,
  isLoading,
  syncingId,
  syncResult,
  onSync,
  onManageIntegrations,
}: Props): JSX.Element {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const hasDateRange = startDate !== '' && endDate !== '';

  return (
    <>
      <PageHeader
        title="Bank Accounts"
        actions={
          <TrioButton
            variantStyle="outline"
            variantColor="gray"
            size="small"
            onClick={onManageIntegrations}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Manage in Settings
          </TrioButton>
        }
      />

      {syncResult && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => {}}>
          Sync complete: {syncResult.created} new transaction{syncResult.created !== 1 ? 's' : ''}{' '}
          imported, {syncResult.updated} updated.
          {syncResult.categorized &&
            (syncResult.categorized.byRules > 0 || syncResult.categorized.byAi > 0) && (
              <>
                {' '}
                — Auto-categorized: {syncResult.categorized.byRules} by rules,{' '}
                {syncResult.categorized.byAi} by AI.
              </>
            )}
        </Alert>
      )}

      {!isLoading && connections.length === 0 && (
        <EmptyState
          title="No bank accounts connected"
          description="Go to Settings → Integrations to connect your bank."
          action={{ label: 'Go to Integrations', onClick: onManageIntegrations }}
        />
      )}

      {connections.map((connection) => (
        <SectionCard
          key={connection.id}
          title={connection.institutionName}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Last synced: {formatLastSynced(connection.lastSyncedAt)}
              </Typography>
              <StatusChip
                status={connection.status}
                statusMap={{
                  active: { label: 'Active', color: '#10B981' },
                  disconnected: { label: 'Disconnected', color: '#EF4444' },
                }}
              />
            </Box>
          }
        >
          <DataTable
            columns={accountColumns}
            data={connection.accounts ?? []}
            isLoading={isLoading}
            rowKey={(r) => r.id}
            emptyMessage="No accounts found for this connection."
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              pt: 2,
              borderTop: '1px solid hsla(0,0%,0%,0.08)',
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <TrioButton
              variantStyle="filled"
              variantColor="blue"
              size="small"
              onClick={() => onSync(connection.id, startDate, endDate)}
              disabled={!hasDateRange || syncingId === connection.id}
            >
              <SyncIcon
                sx={{
                  fontSize: 16,
                  mr: 0.5,
                  animation: syncingId === connection.id ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                }}
              />
              {syncingId === connection.id ? 'Syncing...' : 'Sync Transactions'}
            </TrioButton>
            {!hasDateRange && (
              <Typography variant="caption" color="text.secondary">
                Select a date range to start syncing
              </Typography>
            )}
          </Box>
        </SectionCard>
      ))}
    </>
  );
};

export default FinanceXBankAccountsComponent;
